<template>
  <div>
    <div v-if="!loadingIncident">
      <page-header>
        <template #title>
          <h1 class="page-title">
            Deployment - {{ claimReference }}
            <VulnerabilityLabel v-if="hasVulnerabilities"></VulnerabilityLabel>
          </h1>
        </template>
        <template #actions>
          <incident-download-buttons
            :incident="incident"
            class="btn-group btn-group-sm"
          ></incident-download-buttons>

          <page-header-action
            @click.prevent="moveToClaimMonitoring"
            v-if="!readonly"
          >
            Move to Claim Monitoring
          </page-header-action>

          <page-header-action @click.prevent="toggleNotes">
            Notes
          </page-header-action>

          <page-header-action
            variant="danger"
            @click.prevent="reject()"
            v-if="hasPermission('claims.update') && !readonly"
          >
            Reject
          </page-header-action>
        </template>
      </page-header>

      <claim-handler></claim-handler>
      <deploy-personal-injury
        v-if="showPIServiceAlert()"
      ></deploy-personal-injury>

      <div class="card mb-3">
        <incident-tabs
          :tabs="tabs"
          :source="source"
          :incident="incident"
          :module="module"
          v-on:selected="setActive"
          :active="currentView"
        ></incident-tabs>

        <transition name="component-fade" mode="out-in">
          <div>
            <div class="half-gutter-top" v-if="currentView == 'services'">
              <save-tariff
                v-if="incidentObject && incidentObject.identifier"
                :incident="incidentObject"
                :readonly="readonly"
                key="tariff"
              ></save-tariff>

              <service-tabs
                :incident="incident"
                :module="module"
                :claim-type-id="claimType.id"
                :claim-types="incidentClaimTypes || []"
                v-if="claimType"
                v-on:services="setServices"
                :readonly="readonly"
              >
                <template #hire>
                  <hires
                    :incident="incident"
                    :readonly="readonly"
                    key="hires"
                  ></hires>
                </template>

                <template #repair>
                  <repair
                    :incident="incident"
                    :selectedService="getSelectedService('repair')"
                    :readonly="readonly"
                  ></repair>
                </template>

                <template #salvage>
                  <salvage
                    :incident="incident"
                    :selectedService="getSelectedService('salvage')"
                    :readonly="readonly"
                  ></salvage>
                </template>

                <template #recovery>
                  <recovery
                    :incident="incident"
                    :selectedService="getSelectedService('recovery')"
                    :readonly="readonly"
                  ></recovery>
                </template>

                <template #diminution>
                  <diminution
                    :incident="incident"
                    :selectedService="getSelectedService('diminution')"
                    :readonly="readonly"
                  ></diminution>
                </template>

                <template #personalInjury>
                  <personal-injury
                    :incident="incident"
                    :selectedService="getSelectedService('personal_injury')"
                    :readonly="readonly"
                  ></personal-injury>
                </template>
              </service-tabs>
            </div>

            <incident-diary
              :url="'/deployment/' + incident"
              :incident="incident"
              :readonly="readonly"
              :slim="true"
              key="diary"
              v-if="currentView == 'diary'"
            ></incident-diary>

            <incident-source
              :incident="incident"
              :showCancel="false"
              :readonly="readonly"
              v-on:created="activateTabs"
              v-on:exists="activateTabs"
              v-if="currentView == 'source'"
              v-on:unsaved-data="saveStatus"
              key="source"
            ></incident-source>

            <accident-details
              :incident="incident"
              :readonly="readonly"
              v-if="currentView == 'accident_description'"
              v-on:unsaved-data="saveStatus"
              key="accidentDetails"
            ></accident-details>

            <client
              :incident="incident"
              :module="module"
              :readonly="readonly"
              v-if="currentView == 'client'"
              v-on:unsaved-data="saveStatus"
              key="client"
            ></client>

            <third-parties
              :incident="incident"
              :module="module"
              :readonly="readonly"
              v-if="currentView == 'third_parties'"
              v-on:unsaved-data="saveStatus"
              key="third_parties"
            ></third-parties>

            <witnesses
              :incident="incident"
              :readonly="readonly"
              v-if="currentView == 'witnesses'"
              key="witnesses"
            ></witnesses>

            <estimate
              :incident="incident"
              :readonly="readonly"
              v-if="currentView == 'estimate'"
              v-on:unsaved-data="saveStatus"
            ></estimate>

            <statements
              :incident="incident"
              :readonly="readonly"
              v-if="currentView == 'statements'"
              v-on:unsaved-data="saveStatus"
            ></statements>

            <documents
              :incident="incident"
              :readonly="readonly"
              v-if="currentView == 'documents'"
              key="documents"
            ></documents>

            <jobs
              :incident="source.incident_id"
              v-if="currentView == 'jobs'"
              key="jobs"
            ></jobs>

            <email
              :incident="incident"
              v-if="currentView == 'email'"
              key="email"
            ></email>
          </div>
        </transition>
        <div
          class="card-footer text-right"
          v-if="currentView == 'details' && !readonly"
        >
          <button
            class="btn btn-primary btn-raised"
            name="save"
            v-on:click.prevent="submit"
          >
            <i class="icon icon-save"></i> Save
          </button>
        </div>
      </div>
    </div>
    <div v-else>
      <preloader></preloader>
    </div>

    <notes-window
      :show="notes"
      :incident="incident"
      v-on:toggle-note="toggleNotes"
    ></notes-window>
  </div>
</template>

<script>
// import Deployment from './Children/Deployment.vue';
import Hires from './Hires.vue';
import IncidentDiary from './../Shared/IncidentDiary.vue';
import Source from './../FirstNoticeOfLoss/Sections/Source.vue';
import AccidentDetails from './../FirstNoticeOfLoss/Sections/AccidentDetails.vue';
import Client from './../FirstNoticeOfLoss/Sections/Client.vue';
import ThirdParties from './../FirstNoticeOfLoss/Sections/ThirdParties.vue';
import Witnesses from './../FirstNoticeOfLoss/Sections/Witnesses.vue';
import Notes from './../Shared/Notes.vue';
import Documents from './../Shared/Documents.vue';
import Email from './../Shared/Email.vue';
import IncidentTabs from './../Shared/IncidentTabs.vue';
import IncidentDownloadButtons from './../Shared/IncidentDownloadButtons.vue';
import ServiceTabs from './../Shared/ServiceTabs.vue';
import Repair from './Repair';
import Salvage from './Salvage';
import Recovery from './Recovery';
import Diminution from './Diminution';
import PersonalInjury from './PersonalInjury';
import Jobs from './../FirstNoticeOfLoss/Sections/Jobs.vue';
import VulnerabilityLabel from '@/components/Modules/Shared/VulnerabilityLabel.vue';
import Statements from '@/components/Modules/FirstNoticeOfLoss/Sections/Statements.vue';
import Estimate from '@/components/Modules/FirstNoticeOfLoss/Sections/Estimate.vue';
import SaveTariff from '@/components/Modules/Shared/SaveTariff.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    }
  },

  components: {
    VulnerabilityLabel,
    PageHeader,
    PageHeaderAction,
    Estimate,
    Statements,
    Hires,
    Repair,
    Salvage,
    Recovery,
    Diminution,
    PersonalInjury,
    IncidentDiary,
    'incident-source': Source,
    AccidentDetails,
    Client,
    ThirdParties,
    Witnesses,
    'notes-window': Notes,
    Documents,
    Email,
    IncidentTabs,
    IncidentDownloadButtons,
    ServiceTabs,
    'save-tariff': SaveTariff,
    Jobs
  },

  data() {
    return {
      module: 'Deployment',
      deploymentComplete: true,
      notes: false,
      adviceSent: false,
      currentView: this.$route.hash
        ? this.$route.hash.replace('#', '')
        : 'services',
      unsaved_data: false,
      referringToAgent: false,
      downloading: false,
      hireData: {},
      reasons: [],
      services: [],
      tabs: [
        {
          name: 'services',
          label: 'Services',
          disabled: false
        },
        {
          name: 'diary',
          label: 'Diary',
          disabled: false
        },
        {
          name: 'jobs',
          label: 'Jobs',
          disabled: false
        }
      ]
    };
  },

  computed: {
    incidentObject() {
      return this.$store.getters['Incident/incident'];
    },

    readonly() {
      return this.isReadonly();
    },

    claimType() {
      return this.$store.getters['Incident/incidentClaimType'];
    },

    incidentClaimTypes() {
      return this.$store.getters['IncidentClaimTypes/all'];
    },

    source() {
      return this.$store.getters['Incident/source'];
    },

    claimReference() {
      const source = this.$store.getters['Incident/source'];
      if (source && source.claim_reference) {
        return source.claim_reference;
      }
      return null;
    },

    deployedServices() {
      return this.$store.getters['Incident/services'];
    },

    loadingIncident() {
      return this.$store.getters['Incident/loading'];
    },

    hasVulnerabilities() {
      const vulnerabilities =
        this.$store.getters['Incident/vulnerabilities'] || [];
      return vulnerabilities.length > 0;
    }
  },

  mounted() {
    this.$store.dispatch('Incident/retrieve', this.incident);

    window.Ajax.get(`/incidents/reasons?filter[status]=rejected`)
      .then(response => {
        this.reasons = response.data;
      })
      .catch(error => {});
  },

  methods: {
    setServices(services) {
      this.services = services;
    },
    showDiary() {
      if (this.currentView !== 'diary') {
        this.currentView = 'diary';
      }
    },

    toggleNotes() {
      this.notes = !this.notes;
    },

    setActive(tab) {
      // check if unsaved data on the current form
      if (this.unsaved_data && this.currentView !== tab) {
        this.saveWarning(tab); // show alert if there is
      } else {
        this.currentView = tab;
      }
    },

    getSelectedService(serviceName) {
      let selectedService = this.services.find(
        service => service.name === serviceName
      );

      if (
        selectedService &&
        this.deployedServices &&
        this.deployedServices.length
      ) {
        return this.deployedServices.find(
          service => service.service_id === selectedService.id
        );
      }
      return null;
    },
    showPIServiceAlert() {
      const selectedService = this.services.find(
        service => service.name === 'personal_injury'
      );

      if (selectedService) {
        const serviceDeployed = this.deployedServices.find(
          service => service.service_id === selectedService.id
        );

        return !(serviceDeployed && this.deployedServices.length);
      }

      return false;
    },

    activateTabs() {
      this.tabs.forEach(tab => {
        tab.disabled = false;
      });
    },

    adviceHasBeenSent() {
      this.adviceSent = true;
    },

    saveStatus(event) {
      this.unsaved_data = event;
    },

    moveToClaimMonitoring() {
      if (this.readonly) {
        return;
      }
      // If the user does not have permission to update the claim then just redirect to the claim monitoring module
      if (!this.hasPermission('claims.update')) {
        this.route(`/claim-monitoring/${this.incident}`);
        return;
      }

      window.Ajax.post(`/incidents/${this.incident}/claim-monitoring`)
        .then(response => {
          this.route(`/claim-monitoring/${this.incident}`);
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            'Sorry, there was an issue moving the incident to claim monitoring.'
          );
        });
    },

    saveWarning(tab) {
      window
        .swal({
          title: 'Are you sure?',
          text:
            'You will lose all details entered in this section if you continue!',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continue without saving',
          cancelButtonText: 'Cancel and stay in this section'
        })
        .then(
          () => {
            // Go to selected tab discarding changes
            this.currentView = tab;
            this.unsaved_data = false;
          },
          dismiss => {}
        );
    },

    referToAgentCheck() {
      window
        .swal({
          title: 'Are you sure?',
          text: `This will instruct the agent to manage this case for you`,
          type: 'warning',
          showCancelButton: true
        })
        .then(() => {
          let agent = this.$store.getters.agent;
          if (agent) {
            this.referringToAgent = true;
            window.Ajax.post(
              `/incidents/${this.incident}/instruct-agent/${agent.identifier}`
            )
              .then(response => {
                window.Ajax.post(
                  `/first-notice-of-loss/${this.incident}/complete`
                )
                  .then(response => {
                    window.toastify.success(
                      'Success!',
                      'The agent was instructed successfully!'
                    );
                    this.$router.push('/deployment');
                    this.referringToAgent = false;
                  })
                  .catch(error => {
                    this.referringToAgent = false;
                    window.toastify.error(
                      'Error',
                      "Sorry, we couldn't instruct the agent"
                    );
                  });
              })
              .catch(error => {
                this.referringToAgent = false;
                window.toastify.error(
                  'Error',
                  "Sorry, we couldn't instruct the agent"
                );
              });
          }
        })
        .catch(error => {});
    },

    reject() {
      if (this.readonly) {
        return;
      }
      const options = {};

      this.reasons.forEach((reason, key) => {
        options[key] = reason.body;
      });

      window
        .swal({
          title: 'Please Confirm',
          text: 'Are you sure you want reject this claim?',
          type: 'warning',
          input: 'select',
          inputOptions: options,
          inputPlaceholder: 'Select a Reason',
          inputValidator: value => {
            return new Promise((resolve, reject) => {
              if (value) {
                resolve(value);
              } else {
                reject('You must select a reason.');
              }
            });
          },
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        })
        .then(reason => {
          const reasonId = this.reasons[reason]?.id;

          if (reasonId) {
            window.Ajax.delete(`/incidents/${this.incident}?reason=${reasonId}`)
              .then(response => {
                window.toastify.success(
                  'Success',
                  'The claim was rejected successfully!'
                );
                this.route(`/deployment`);
              })
              .catch(error => {
                window.toastify.error(
                  'Error',
                  'Sorry, there was an issue rejecting this claim.'
                );
              });
          }
        })
        .catch(() => {});
    }
  },

  beforeDestroy() {
    // this.$store.commit('Incident/clearRecord');
  }
};
</script>
